/**
 * Authentication and profile state.
 *
 * Context rather than a store library: there are exactly two pieces of global state and
 * both change rarely.
 *
 * On mount the app tries a silent refresh. There is no access token after a reload — it
 * only ever lived in memory — so this is what turns the httpOnly cookie back into a
 * usable session without the user seeing a login screen.
 */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import type { AuthResponse, ProfileDto, UserDto } from '@filmodasi/shared';
import {
  api,
  isPackagedClient,
  setAccessToken,
  setAuthLostHandler,
  setProfileId,
  setRefreshToken,
  storedProfileId,
  storedRefreshToken,
} from '../api/client';

interface SessionValue {
  user: UserDto | null;
  profile: ProfileDto | null;
  /** True until the initial silent refresh has resolved one way or the other. */
  loading: boolean;
  login: (email: string, password: string) => Promise<void>;
  register: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  chooseProfile: (profile: ProfileDto | null) => void;
  /**
   * Adopts a session the television was handed by pairing.
   *
   * Separate from `login` because there are no credentials involved: the phone already
   * proved who the household is, and the panel is presenting the result. It goes through
   * `applyAuth` like every other path, so the rule that only a packaged client ever stores
   * a refresh token is enforced in one place rather than two.
   */
  adoptSession: (res: {
    user: UserDto;
    accessToken: string;
    refreshToken?: string;
    profileId: string | null;
  }) => void;
}

const SessionContext = createContext<SessionValue | null>(null);

export function SessionProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<UserDto | null>(null);
  const [profile, setProfile] = useState<ProfileDto | null>(null);
  const [loading, setLoading] = useState(true);

  const chooseProfile = useCallback((next: ProfileDto | null) => {
    setProfile(next);
    setProfileId(next?.id ?? null);
  }, []);

  /*
   * Takes the three fields it actually reads rather than a whole `AuthResponse`.
   *
   * Pairing produces a session without `accessTokenExpiresAt` — the panel is handed a
   * token, not a login result — and widening this is better than casting at the call site,
   * which would have hidden a genuinely missing field just as effectively.
   */
  const applyAuth = useCallback((res: Pick<AuthResponse, 'user' | 'accessToken' | 'refreshToken'>) => {
    setAccessToken(res.accessToken);
    // Present only from the packaged-Tizen endpoints; `setRefreshToken` is itself a no-op
    // on the web, so there is no path by which a cookie client starts storing one.
    if (res.refreshToken) setRefreshToken(res.refreshToken);
    setUser(res.user);
  }, []);

  const logout = useCallback(async () => {
    await api('/api/auth/logout', { method: 'POST', withoutProfile: true }).catch(() => {});
    setAccessToken(null);
    setRefreshToken(null);
    setUser(null);
    chooseProfile(null);
  }, [chooseProfile]);

  useEffect(() => {
    setAuthLostHandler(() => {
      setAccessToken(null);
      // The stored token is what failed; keeping it would retry it on every reload and
      // leave the TV stuck on a login screen it never explains.
      setRefreshToken(null);
      setUser(null);
    });
    return () => setAuthLostHandler(null);
  }, []);

  // Silent restore. A failure here is the normal "not signed in" case, not an error.
  useEffect(() => {
    let cancelled = false;

    // `void`: the IIFE swallows everything in its own `catch`, so there is nothing left to
    // await or handle — but an effect body cannot be async, and an un-discarded promise
    // here is indistinguishable from one somebody forgot to await.
    void (async () => {
      try {
        const res = await api<AuthResponse>('/api/auth/refresh', {
          method: 'POST',
          // The web sends nothing and the cookie carries it. The packaged client has no
          // cookie, so it sends the token it stored at login.
          body: { refreshToken: storedRefreshToken() ?? undefined },
          noRetry: true,
          withoutProfile: true,
        });
        if (cancelled) return;
        applyAuth(res);

        const saved = storedProfileId();
        if (saved) {
          const profiles = await api<ProfileDto[]>('/api/profiles', { withoutProfile: true });
          const match = profiles.find((p) => p.id === saved);
          if (match && !cancelled) chooseProfile(match);
        }
      } catch {
        /* not signed in */
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [applyAuth, chooseProfile]);

  const value = useMemo<SessionValue>(
    () => ({
      user,
      profile,
      loading,
      chooseProfile,
      logout,
      async login(email, password) {
        // Two endpoints, one of which returns a refresh token in the body. That is the
        // only difference, and it is gated server-side on the client-type header the
        // packaged build sends — a web client calling it would be refused.
        applyAuth(
          await api<AuthResponse>(
            isPackagedClient ? '/api/auth/tizen/login' : '/api/auth/login',
            { method: 'POST', body: { email, password }, withoutProfile: true },
          ),
        );
      },
      async register(email, password) {
        applyAuth(
          await api<AuthResponse>('/api/auth/register', {
            method: 'POST',
            body: { email, password },
            withoutProfile: true,
          }),
        );
      },
      adoptSession(res) {
        applyAuth(res);
        /*
         * The profile the approving phone chose is remembered but not resolved here — the
         * profile picker loads the list anyway, and fetching it now would race the access
         * token that `applyAuth` has only just set. Storing the id is enough: the silent
         * restore on the next render is the same path a reload takes.
         */
        setProfileId(res.profileId);
      },
    }),
    [user, profile, loading, chooseProfile, logout, applyAuth],
  );

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}

export function useSession(): SessionValue {
  const ctx = useContext(SessionContext);
  if (!ctx) throw new Error('useSession must be used inside SessionProvider');
  return ctx;
}
